import { create } from 'zustand';
import type { Project, Version, Task, VehicleNodes } from '@/types';
import { projectsApi, versionsApi, timingApi } from '@/api/client';

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  currentVersion: Version | null;
  versions: Version[];
  tasks: Task[];
  isLoading: boolean;
  isProjectSwitching: boolean;
  error: string | null;
  
  // Actions
  loadProjects: () => Promise<void>;
  loadProject: (id: string) => Promise<void>;
  createProject: (project: Omit<Project, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<Project>;
  updateProject: (id: string, data: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
  updateVehicleNodes: (nodes: VehicleNodes) => void;
  
  // Versions
  loadVersions: (projectId: string) => Promise<void>;
  createVersion: (version: Omit<Version, 'id' | 'created_at'>) => Promise<void>;
  deleteVersion: (projectId: string, version: string, versionId: string) => Promise<void>;
  setCurrentVersion: (version: Version | null) => void;
  
  // Tasks
  setTasks: (tasks: Task[]) => void;
  generateTiming: (nodes: VehicleNodes) => Promise<void>;
  updateTask: (taskId: string, data: Partial<Task>) => void;
  addTask: (task: Task) => void;
  deleteTask: (taskId: string) => void;
}

export const useProjectStore = create<ProjectState>()((set, get) => ({
  projects: [],
  currentProject: null,
  currentVersion: null,
  versions: [],
  tasks: [],
  isLoading: false,
  isProjectSwitching: false,
  error: null,

  loadProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const projects = await projectsApi.getAll();
      set({ projects });
    } catch (error) {
      console.error('Load projects error:', error);
      set({ error: 'Failed to load projects' });
    } finally {
      set({ isLoading: false });
    }
  },

  loadProject: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const project = await projectsApi.getById(id);
      set({ currentProject: project });
    } catch (error) {
      console.error('Load project error:', error);
      set({ error: 'Failed to load project' });
    } finally {
      set({ isLoading: false });
    }
  },

  createProject: async (project) => {
    set({ isLoading: true, error: null });
    try {
      const newProject = await projectsApi.create(project);
      set((state) => ({ 
        projects: [...state.projects, newProject],
        currentProject: newProject 
      }));
      return newProject;
    } catch (error) {
      console.error('Create project error:', error);
      set({ error: 'Failed to create project' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateProject: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const updatedProject = await projectsApi.update(id, data);
      set((state) => ({
        projects: state.projects.map((p) => (p.id === id ? updatedProject : p)),
        currentProject: state.currentProject?.id === id ? updatedProject : state.currentProject,
      }));
    } catch (error) {
      console.error('Update project error:', error);
      set({ error: 'Failed to update project' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await projectsApi.delete(id);
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
      }));
    } catch (error) {
      console.error('Delete project error:', error);
      set({ error: 'Failed to delete project' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  setCurrentProject: (project) => {
    set({
      currentProject: project,
      tasks: project?.taskList || [],
      currentVersion: null, // 重置版本
      versions: [], // 清空版本列表，切换项目后重新加载
    });

    // 始终触发事件，让 Dashboard 知道项目变化了
    window.dispatchEvent(new CustomEvent('projectChanged', {
      detail: {
        projectId: project?.id,
        taskList: project?.taskList || [],
        nodes: project?.nodes || {}
      }
    }));
  },

  updateVehicleNodes: (nodes) => {
    const currentProject = get().currentProject;
    if (currentProject) {
      set({ 
        currentProject: { ...currentProject, nodes }
      });
      
      // 触发事件通知 Dashboard 节点已更改（不重置 originalDataRef）
      // 这样 Dashboard 的 hasNodesChanged 检测会返回 true
      window.dispatchEvent(new CustomEvent('nodesChanged', {
        detail: {
          projectId: currentProject.id,
          nodes: nodes
        }
      }));
    }
  },

  loadVersions: async (projectId: string) => {
    try {
      const versions = await versionsApi.getAll(projectId);
      set({ versions });
    } catch (error) {
      console.error('Load versions error:', error);
    }
  },

  createVersion: async (version) => {
    try {
      const newVersion = await versionsApi.create(version);
      set((state) => ({ versions: [...state.versions, newVersion] }));
    } catch (error) {
      console.error('Create version error:', error);
      throw error;
    }
  },

  deleteVersion: async (projectId: string, version: string, versionId: string) => {
    try {
      await versionsApi.delete(projectId, version);
      set((state) => ({
        versions: state.versions.filter((v) => v.id !== versionId),
        currentVersion: state.currentVersion?.id === versionId ? null : state.currentVersion,
      }));
    } catch (error) {
      console.error('Delete version error:', error);
      throw error;
    }
  },

  setCurrentVersion: (version) => set({ currentVersion: version }),

  setTasks: (tasks) => set({ tasks }),

  generateTiming: async (nodes) => {
    set({ isLoading: true });
    try {
      const tasks = await timingApi.generate(nodes);
      set({ tasks });
    } catch (error) {
      console.error('Generate timing error:', error);
      set({ error: 'Failed to generate timing' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateTask: (taskId, data) => {
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === taskId ? { ...t, ...data } : t)),
    }));
  },

  addTask: (task) => {
    set((state) => ({ tasks: [...state.tasks, task] }));
  },

  deleteTask: (taskId) => {
    set((state) => ({
      tasks: state.tasks.filter((t) => t.id !== taskId),
    }));
  },
}));
